import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { AnimatedEmblem } from "../components/shop/AnimatedEmblem";
import { PasswordStrength, TrustRow, codeCls, pwScore } from "../components/security/TwoFactor";
import { inputCls } from "../components/community/JoinSteps";

export const Shell = ({ children, testid }) => (
  <div className="bg-[#F7F5FB] min-h-[75vh] grid place-items-center px-4 py-16">
    <div className="w-full max-w-md bg-white rounded-[28px] border border-[#120E1F]/5 p-8 sm:p-10 space-y-5 shadow-[0_30px_60px_-30px_rgba(18,14,31,0.2)]" data-testid={testid}>
      <AnimatedEmblem className="h-16 w-16" testid={`${testid}-emblem`} />{children}<TrustRow />
    </div>
  </div>
);

export function ForgotPassword() {
  const { tr } = useLang();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true); try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); } };
  return (
    <Shell testid="forgot-password-page">
      <h1 className="font-display text-4xl">{tr("Passwort vergessen?", "Forgot password?")}</h1>
      {sent ? <p className="flex gap-2 text-sm" data-testid="forgot-sent"><MailCheck className="h-5 w-5 text-[#2E9E6B] shrink-0" />{tr("Falls ein Konto mit dieser E-Mail existiert, haben wir dir einen Link zum Zurücksetzen geschickt (1 Stunde gültig). Prüfe auch den Spam-Ordner.", "If an account exists for this email, we've sent a reset link (valid 1 hour). Check your spam folder too.")}</p> : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-[#707783]">{tr("Gib deine E-Mail ein – wir schicken dir einen sicheren Link.", "Enter your email – we'll send you a secure link.")}</p>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="deine@email.de" className={inputCls} data-testid="forgot-email-input" />
          <button disabled={busy} className="btn-dark w-full h-12" data-testid="forgot-submit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : tr("Link senden", "Send link")}</button>
        </form>)}
      <Link to="/login" className="block text-center text-sm text-[#6E3FB8]">← {tr("Zurück zur Anmeldung", "Back to sign in")}</Link>
    </Shell>
  );
}

export function ResetPassword() {
  const { tr } = useLang();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const mismatch = f.confirm && f.password !== f.confirm;
  const weak = pwScore(f.password) < 2;
  const token = params.get("token");
  const submit = async (e) => {
    e.preventDefault(); if (mismatch || weak) return; setBusy(true);
    try { await api.post("/auth/reset-password", { token: params.get("token") || "", password: f.password }); toast.success(tr("Passwort gespeichert – bitte neu anmelden.", "Password saved – please sign in.")); nav("/login"); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell testid="reset-password-page">
      <h1 className="font-display text-4xl">{tr("Neues Passwort", "New password")}</h1>
      {!token ? <p className="text-sm" data-testid="reset-no-token">{tr("Dieser Link ist unvollständig.", "This link is incomplete.")} <Link to="/forgot-password" className="text-[#6E3FB8] underline">{tr("Neuen Link anfordern", "Request a new link")}</Link></p> : <form onSubmit={submit} className="space-y-3">
        <input type="password" required minLength={8} placeholder={tr("Neues Passwort", "New password")} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} data-testid="reset-password-input" />
        <PasswordStrength pw={f.password} />
        <input type="password" required placeholder={tr("Passwort bestätigen", "Confirm password")} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} className={inputCls} data-testid="reset-confirm-input" />
        {mismatch && <p className="text-xs text-red-600">{tr("Passwörter stimmen nicht überein.", "Passwords don't match.")}</p>}
        <button disabled={busy || mismatch || weak} className="btn-dark w-full h-12" data-testid="reset-submit-btn">{tr("Passwort speichern", "Save password")}</button>
      </form>}
      <p className="text-xs text-[#707783]">{tr("Aus Sicherheitsgründen wirst du danach auf allen Geräten abgemeldet.", "For security you'll be signed out on all devices.")}</p>
    </Shell>
  );
}

export function VerifyEmail() {
  const { tr } = useLang();
  const { refresh, user } = useAuth();
  const [params] = useSearchParams();
  const [state, setState] = useState("loading");
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return; ran.current = true;
    api.post("/auth/verify-email", { token: params.get("token") || "" }).then(() => { setState("ok"); refresh().catch(() => {}); }).catch(() => setState("error"));
  }, [params, refresh]);
  return (
    <Shell testid="verify-email-page">
      {state === "loading" && <Loader2 className="h-6 w-6 animate-spin" />}
      {state === "ok" && <p className="flex items-center gap-2 font-display text-3xl" data-testid="verify-ok"><CheckCircle2 className="h-7 w-7 text-[#2E9E6B]" />{tr("E-Mail bestätigt!", "Email confirmed!")}</p>}
      {state === "error" && <p className="flex items-center gap-2 text-sm" data-testid="verify-error"><XCircle className="h-5 w-5 text-red-600" />{tr("Der Link ist ungültig oder abgelaufen. Fordere im Profil unter Sicherheit einen neuen an.", "The link is invalid or expired. Request a new one in your profile under Security.")}</p>}
      <Link to={user ? "/member/security" : "/login"} className="btn-dark w-full h-12" data-testid="verify-continue">{tr("Weiter", "Continue")}</Link>
    </Shell>
  );
}

export const TwoFactorLogin = ({ challenge, onDone, onCancel }) => {
  const { tr } = useLang();
  const { loginTwoFA } = useAuth();
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { onDone(await loginTwoFA(challenge.challenge, code, backup ? "backup" : "auto")); } catch (err) { toast.error(errMsg(err)); setCode(""); } finally { setBusy(false); }
  };
  const resend = () => api.post("/auth/login/2fa/resend", { challenge: challenge.challenge }).then(() => toast.success(tr("Neuer Code gesendet", "New code sent"))).catch((e) => toast.error(errMsg(e)));
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="twofa-login-form">
      <h1 className="font-display text-4xl">{tr("Bestätigung", "Verification")}</h1>
      <p className="text-sm text-[#707783]">{backup ? tr("Gib einen deiner Backup-Codes ein (z.B. A1B2-C3D4).", "Enter one of your backup codes (e.g. A1B2-C3D4).")
        : challenge.method === "totp" ? tr("Gib den 6-stelligen Code aus deiner Authenticator-App ein.", "Enter the 6-digit code from your authenticator app.")
          : challenge.code_sent === false ? tr("Gib den Code aus deiner E-Mail ein.", "Enter the code from your email.") : `${tr("Wir haben einen Code an", "We sent a code to")} ${challenge.email_hint} ${tr("gesendet.", ".")}`}</p>
      {challenge.code_sent === false && !backup && <p className="text-xs text-[#B8860B]" data-testid="twofa-code-not-sent">{tr("Der Code konnte gerade nicht gesendet werden. Tippe auf „Code erneut senden“ oder nutze einen Backup-Code.", "The code couldn't be sent right now. Tap \"Resend code\" or use a backup code.")}</p>}
      <input autoFocus required value={code} onChange={(e) => setCode(backup ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode={backup ? "text" : "numeric"} autoComplete="one-time-code" placeholder={backup ? "XXXX-XXXX" : "000000"} className={codeCls} data-testid="twofa-login-code-input" />
      <button disabled={busy} className="btn-dark w-full h-12" data-testid="twofa-login-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : tr("Anmelden", "Sign in")}</button>
      <div className="flex justify-between text-xs">
        <button type="button" onClick={() => { setBackup(!backup); setCode(""); }} className="text-[#6E3FB8] underline" data-testid="twofa-use-backup">{backup ? tr("Normalen Code verwenden", "Use normal code") : tr("Backup-Code verwenden", "Use a backup code")}</button>
        {challenge.method === "email" && !backup && <button type="button" onClick={resend} className="text-[#6E3FB8] underline" data-testid="twofa-resend">{tr("Code erneut senden", "Resend code")}</button>}
        <button type="button" onClick={onCancel} className="text-[#707783]">{tr("Abbrechen", "Cancel")}</button>
      </div>
    </form>
  );
};
